import { kissmetricsRecordingEvent } from 'utils/kissmetrics'

import { trackFacebookEvent } from '../facebook'
import { trackGTMEvent } from '../gtm'
import { trackHotjarEvent } from '../hotjar'

import {
  EVENTS,
  HOTJAR_EVENTS,
  MIXPANEL_NORMALIZED_EVENTS,
  KISSMETRICS_NORMALIZED_EVENTS,
  FACEBOOK_EVENTS,
  GTM_EVENTS,
} from './consts'

const hasEvent = <T extends Record<string, unknown>>(
  event: keyof T | string,
  eventsConst: T = {} as T,
): event is keyof T => {
  return event in eventsConst
}

/**
  @description Centralized event tracking function that dispatches events to multiple analytics platforms
  {@link https://ubersuggest.atlassian.net/wiki/spaces/engineering/pages/1161789477/Event+Tracker | See tracking events documentation}
  @param params - The tracking parameters
  @param params.event - The event name from {@link EVENTS} enum
  @param params.mixpanelProperties - Array of properties for Mixpanel
  @param params.kissmetricsProperties - Object of properties for Kissmetrics
  @param params.gtmProperties - Object of properties for Google Tag Manager
  @param params.facebookProperties - Object of properties for Facebook
*/
export const trackEvent = ({
  event,
  mixpanelProperties = [],
  kissmetricsProperties = {},
  gtmProperties = {},
  facebookProperties,
}: {
  event: (typeof EVENTS)[keyof typeof EVENTS]
  mixpanelProperties?: unknown[]
  kissmetricsProperties?: Record<string, unknown>
  gtmProperties?: Record<string, unknown>
  facebookProperties?: Record<string, unknown>
}) => {
  if (hasEvent(event, HOTJAR_EVENTS)) {
    trackHotjarEvent(HOTJAR_EVENTS[event])
  }
  if (hasEvent(event, MIXPANEL_NORMALIZED_EVENTS)) {
    // @ts-expect-error - Mixpanel methods accept these parameters
    MIXPANEL_NORMALIZED_EVENTS[event](...mixpanelProperties)
  }

  const { shouldTrigger = false, ...restKissmetricsProperties } =
    kissmetricsProperties

  if (
    hasEvent(event, KISSMETRICS_NORMALIZED_EVENTS) &&
    (Object.keys(restKissmetricsProperties).length > 0 || shouldTrigger)
  ) {
    kissmetricsRecordingEvent(
      KISSMETRICS_NORMALIZED_EVENTS[event],
      restKissmetricsProperties,
    )
  }

  const hasGtmProperties = Object.keys(gtmProperties || {}).length > 0

  if (hasEvent(event, GTM_EVENTS) && hasGtmProperties) {
    trackGTMEvent({
      event: GTM_EVENTS[event],
      ...gtmProperties,
    })
  }

  const hasFacebookProperties = Object.keys(facebookProperties || {}).length > 0

  if (
    hasEvent(event, FACEBOOK_EVENTS) &&
    (hasFacebookProperties || hasGtmProperties)
  ) {
    trackFacebookEvent(
      FACEBOOK_EVENTS[event],
      facebookProperties || gtmProperties,
    )
  }
}
